import React, {createRef, ReactElement, RefObject} from 'react';
import MComponent from '../Toolkit/Components/Component';
import {Column} from 'primereact/column';
import MTable, {MTableFilters} from '../Toolkit/Components/Controls/Table';
import NSwagAdapter from '../Toolkit/Api/NSwagAdapter';
import MNSwagClientConfiguration from '../Toolkit/Api/NSwagClientConfiguration';
import TrafficManagementAccessTokenProvider from '../Api/TrafficManagementAccessTokenProvider';
import {IMsalContext} from '@azure/msal-react';
import {Invoice, InvoiceMultiReply, InvoiceReply} from '../Api/ControllerTypes/InvoiceTypes';
import {CreateInvoiceRequest, InvoiceClient, UpdateInvoiceRequest} from '../Api/InvoiceClient';
import InvoiceDialog from '../Dialogs/InvoiceDialog';
import MButton from '../Toolkit/Components/Controls/Button';
import {MNewIconName} from '../Toolkit/Icons';
import MSpacer from '../Toolkit/Components/Controls/Spacer';
import MToolbar from '../Toolkit/Components/Controls/Toolbar';
import {SequenceClient} from '../Api/SequenceClient';
import {PoNumberReply} from '../Api/ControllerTypes/SequenceTypes';
import EnumContext from '../Context/enum-context';

type InvoiceRow = {
    data: Invoice;
}

type Props = {
    parentId: string;
    msalContext: IMsalContext;
}

/** */
type State = {
    rowData: InvoiceRow[];
    filters: MTableFilters;
    selectedRow?: InvoiceRow;
}

class InvoiceControl extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>;

    private readonly m_invoiceRef: RefObject<InvoiceDialog>;
    private readonly m_invoiceClient: NSwagAdapter<InvoiceClient>;
    private readonly m_sequenceClient: NSwagAdapter<SequenceClient>;

    public constructor(props: Props) {
        super(props);

        this.m_invoiceRef = createRef<InvoiceDialog>();
        this.m_invoiceClient = new NSwagAdapter<InvoiceClient>(new InvoiceClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_sequenceClient = new NSwagAdapter<SequenceClient>(new SequenceClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));

        this.bindMethods();
        this.initializeState();
    }

    private bindMethods(): void {
        this.table_onSelectionChange = this.table_onSelectionChange.bind(this);
        this.loadPageData = this.loadPageData.bind(this);
        this.isPlanner = this.isPlanner.bind(this);
        this.onNew_Click = this.onNew_Click.bind(this);
        this.onUpdate_Click = this.onUpdate_Click.bind(this);
        this.onInvoiceUpdate = this.onInvoiceUpdate.bind(this);
        this.feeTypeTemplate = this.feeTypeTemplate.bind(this);
    }

    async componentDidMount() {
        await this.loadPageData();
    }

    private table_onSelectionChange(selection: InvoiceRow | InvoiceRow[] | undefined): void {

        this.safeSetState((draft: State) =>
            draft.selectedRow = selection as InvoiceRow | undefined);

    }

    private initializeState(): void {
        this.state = {
            filters: {},
            rowData: []
        };
    }

    private isPlanner(): boolean {
        const allowedRoles: string[] = ['Booking', 'Supervisor'];
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const access = allowedRoles.some(a => roles?.includes(a));

        return !access;
    }

    private async onNew_Click(isCreditCard: boolean): Promise<void> {
        let poNumber = 'CC Payment';
        if(!isCreditCard) {
            const seqReply = await this.m_sequenceClient.callWithReply(
                PoNumberReply, (client => client.getNextPoNumber()));
            seqReply.throwIfFailed();
            poNumber = seqReply.reply!.poNumber
        }

        const newInvoice: Invoice = {
            id: -1,
            parentId: this.props.parentId,
            poNumber: poNumber,
            applicationSent: false,
            workStart: new Date(),
            workEnd: new Date(),
            charge: 0,
            invoiceNumber: '',
            invoiced: false,
            councilId: 0,
            feeType: 0,
            notes: '',
            modifiedOn: new Date(),
            modifiedBy: ''
        }

        this.m_invoiceRef.current!.show(true, newInvoice, this.onInvoiceUpdate);
    }

    private onUpdate_Click(): void {
        this.m_invoiceRef.current!.show(false, this.state.selectedRow!.data, this.onInvoiceUpdate);
    }

    private async onInvoiceUpdate(newData: boolean, data: Invoice) {
        if(newData) {
            const invoiceReply = await this.m_invoiceClient.callWithReply(
                InvoiceReply, (client => client.create(new CreateInvoiceRequest({
                    parentId: data.parentId,
                    applicationSent: data.applicationSent,
                    poNumber: data.poNumber,
                    workStart: data.workStart!,
                    workEnd: data.workEnd!,
                    charge: data.charge,
                    invoiceNumber: data.invoiceNumber,
                    invoiced: data.invoiced,
                    councilId: data.councilId,
                    feeType: data.feeType,
                    notes: data.notes
                })))
            );

            invoiceReply.throwIfFailed();
        } else {
            const invoiceReply = await this.m_invoiceClient.callWithReply(
                InvoiceReply, (client => client.update(new UpdateInvoiceRequest({
                    id: data.id,
                    parentId: data.parentId,
                    applicationSent: data.applicationSent,
                    poNumber: data.poNumber,
                    workStart: data.workStart!,
                    workEnd: data.workEnd!,
                    charge: data.charge,
                    invoiceNumber: data.invoiceNumber,
                    invoiced: data.invoiced,
                    councilId: data.councilId,
                    feeType: data.feeType,
                    notes: data.notes
                })))
            );
            invoiceReply.throwIfFailed();
        }

        await this.loadPageData();
    }

    private entryDateTemplate(rowData: InvoiceRow): string | undefined {
        if(rowData.data.modifiedOn !== null) {
            try {
                return rowData.data.modifiedOn!.toLocaleString();
            } catch {
                return '';
            }
        }
        return '';
    }

    private feeTypeTemplate(rowData: InvoiceRow): string | null {
        const index = this.context.enums?.feeTypes.findIndex(value => value.id === rowData.data.feeType);

        if(index === undefined || index === -1) {
            return null;
        }

        return this.context.enums?.feeTypes[index!].name!;
    }

    private noteTemplate(rowData: InvoiceRow): ReactElement {
        return (
                <span style={{whiteSpace: 'normal'}}>
                    {rowData.data.notes}
                </span>

        );
    }

    private async loadPageData(): Promise<void> {

        //Read invoice data.
        const invoiceResult = await this.m_invoiceClient.callWithReply(
            InvoiceMultiReply, (client) => client.getAll(this.props.parentId));
        invoiceResult.throwIfFailed();
        const rowData = invoiceResult.reply!.invoices.map(n => ({data: n}));

        this.safeSetState(draft => {
            draft.rowData = rowData;
        });
    }

    public render(): ReactElement {
        return (
            <React.Fragment>
                <MToolbar>
                    <MButton label="Raise PO"
                             color="success"
                             icon={MNewIconName}
                             disabled={this.isPlanner}
                             onClick={() => this.onNew_Click(false)}/>
                    <MSpacer/>
                    <MButton label="CC Payment"
                             color="success"
                             icon={MNewIconName}
                             disabled={this.isPlanner}
                             onClick={() => this.onNew_Click(true)}/>
                    <MSpacer/>
                    <MButton label="Edit"
                             icon={'pi pi-pencil'}
                             disabled={this.state.selectedRow === undefined || this.isPlanner}
                             onClick={this.onUpdate_Click}/>
                </MToolbar>
                <InvoiceDialog ref={this.m_invoiceRef}
                               councilList={this.context.enums?.councilList ?? []}
                               feeTypes={this.context.enums?.feeTypes ?? []}
                />
                <MTable
                    rowKey="data.id"
                    rowData={this.state.rowData}
                    filters={this.state.filters}
                    onSelectionChange={this.table_onSelectionChange}
                    sortField={'data.modifiedOn'}
                    sortOrder={-1}>

                    <Column field="data.modifiedOn"
                            header="Date"
                            body={this.entryDateTemplate}
                            filter={false}
                            sortable={true}
                            style={{width: '15%'}}/>
                    <Column field="data.modifiedBy"
                            header="User"
                            filter={false}
                            sortable={true}
                            style={{width: '15%'}}/>
                    <Column field="data.poNumber"
                            header="PO Number"
                            filter={false}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column field="data.feeType"
                            header="Fee Type"
                            filter={false}
                            sortable={true}
                            body={this.feeTypeTemplate}
                            style={{width: '10%'}}/>
                    <Column field="data.applicationSent"
                            header="Application Sent"
                            body={(rowData: InvoiceRow) => rowData.data.applicationSent ? 'Yes' : 'No'}
                            filter={false}
                            sortable={true}
                            style={{width: '5%'}}/>
                    <Column field="data.invoiceNumber"
                            header="Invoice Number"
                            filter={false}
                            sortable={true}
                            style={{width: '10%'}}/>
                    <Column field="data.invoiced"
                            header="Invoiced"
                            body={(rowData: InvoiceRow) => rowData.data.invoiced ? 'Yes' : 'No'}
                            filter={false}
                            sortable={true}
                            style={{width: '5%'}}/>
                    <Column field="data.notes"
                            header="Comment"
                            filter={false}
                            sortable={false}
                            body={this.noteTemplate}
                            style={{width: '40%'}}/>
                </MTable>
            </React.Fragment>
        );
    }
}

export default InvoiceControl;